<template>
  <BoxWithTitle
    collapsible
    openingIcon="mdi-chevron-down"
    closingIcon="mdi-chevron-up"
  >
    <template #header>
      <v-toolbar-title>
        {{ $t('maintenanceSchedule.frequencies') }}
      </v-toolbar-title>
    </template>
    <template #content>
      <VTableRemote
        v-if="!loading"
        ref="tableRemoteRef"
        :iconHeader="hasPermission('maintenanceSchedules.frequencies.add') ? 'add' : null"
        :items="displayedfrequencies"
        isSearchable
        :headers="tableHeaders"
        :isCustomIcon="true"
        :itemActions="actions"
        :title="$t('maintenanceSchedule.frequencies')"
        :pagination="pagination"
        :centerColumns="['action']"
        :notSortableColumns="['action']"
        :sortableColumns="['name']"
        hideDefaultFooter
        :isInfinitScroll="true"
        @iconAction="onClickAction"
        @sendQueryParams="fetchQuery"
      >
        <template #withInfiniteScroll>
          <div v-intersect="endIntersect" />
        </template>
      </VTableRemote>
    </template>
  </BoxWithTitle>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import FrequencyForm from '@/components/forms/FrequencyForm';
import BoxWithTitle from '@/components/shared/containers/VBoxWithTitle';
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import { isEmpty } from 'lodash';


export default {
    components: {
        BoxWithTitle,
        VTableRemote,
    },
    data() {
        return {
            frequencies: [],
            loading: false,
            pagination: {},
            queryParams: { perPage: 25 },
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
            'teamMainLang',
            'userLang',
        ]),
        tableHeaders() {
            return [
                'name',
            ].map(header => ({
                text: this.$t(`forms.labels.${header}`),
                value: header,
                sortable: true,
            }));
        },
        actions() {
            const actions = [];

            if (this.hasPermission('maintenanceSchedules.frequencies.edit')) {
                actions.push({
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }

            if (this.hasPermission('maintenanceSchedules.frequencies.remove')) {
                actions.push({
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }

            return actions;
        },
        displayedfrequencies() {
            return this.frequencies.map(({ id, name, occurenceAmount, timeUnit }) => ({
                id,
                name: name[this.userLang] && name[this.userLang].length ? name[this.userLang] : name[this.teamMainLang],
                occurenceAmount,
                timeUnit,
            }));
        },
    },
    async created() {
        this.loading = true;
        await this.getFrequencies();
        this.loading = false;
    },
    methods: {
        async getFrequencies() {
            // for the first time fetch (empty pagination) OR next page
            if (isEmpty(this.pagination) || (this.pagination?.currentPage !== this.queryParams?.page)) {
                const { data } = await this.$rq.getFrequencies(this.queryParams);
                this.frequencies = [...data.frequencies, ...this.frequencies];
                this.pagination = data.pagination;
            }
        },
        async fetchQuery({ order, ...params }) {
            this.queryParams = {
                ...this.queryParams,
                ...params,
                page: 1,
                order,
            };
            this.pagination = {};
            this.frequencies = [];
            await this.getFrequencies();
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('maintenanceSchedule.addFrequency'),
                component: Vue.component('FrequencyForm', FrequencyForm),
                showLangs: true,
                onConfirm: requestPayload => this.addFrequency(requestPayload),
            });
        },
        onClickAction(frequency, action) {
            if (frequency === 'add') {
                this.onClickAdd();
            } else if (action.mode === 'edit') {
                this.$root.$emit('showModal', {
                    title: this.$t('maintenanceSchedule.updateFrequency'),
                    component: Vue.component('FrequencyForm', FrequencyForm),
                    payload: { frequency: this.frequencies.find(fr => fr.id === frequency.id), isEdition: true },
                    showLangs: true,
                    onConfirm: requestPayload => this.updateFrequency(frequency.id, requestPayload),
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('maintenanceSchedule.removeFrequency'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t('maintenanceSchedule.confirmRemoveFrequency') },
                    onConfirm: () => this.removeFrequency(frequency.id),
                    isForm: false,
                });
            } else if (frequency === 'add') {
                this.onClickAdd();
            }
        },
        async addFrequency(requestPayload) {
            const payload = this.$utils.removeEmptyNullOrUndefined(requestPayload, true);
            await this.$rq.addFrequency(payload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.frequencyAdded'),
            });
            await this.fetchQuery({});
        },
        async updateFrequency(id, requestPayload) {
            if (!requestPayload) return;
            await this.$rq.updateFrequency(id, requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.frequencyUpdated'),
            });
            await this.fetchQuery({});
        },
        async removeFrequency(id) {
            await this.$rq.removeFrequency(id);
            this.$toasted.global.toast_success({
                message: this.$t('msg.frequencyRemoved'),
            });
            await this.fetchQuery({});
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getFrequencies();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
};
</script>
